import { inject, Injectable, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { catchError, EMPTY, map, Observable, Subject, switchMap, takeUntil, tap } from 'rxjs';
import { AddPlanMeasurementModelInput, Uuid } from '../../../../../../graphql';
import { QuickFeedbackService } from '../../../../../core/components/quick-feedback/quick-feedback.service';
import { BxLogger } from '../../../../../core/services/bx-logger.service';
import { AsideModalSize, HelperService } from '../../../../../core/services/helper.service';
import { TakeOffService } from '../../../../takeoff';
import { Drawing, DrawingShape, Position, Scale, ScaleUnit } from '../../../../takeoff/core';
import { TakeOffV2StoresService } from '../../../../takeoff/take-off-v2/feat-take-off-v2/domain';
import { TakeOffV2DrawingService } from '../../../../takeoff/take-off-v2/feat-take-off-v2/take-off-v2-drawing.service';
import { EstimateViewStoresService } from '../../shell/domain/apis/estimate-view.stores';
import { EstimateMeasurementStoresService } from '../measurement/feat-estimate-measurements-v2/domain';
import {
    EstimateMeasurement,
    EstimateMeasurementVM,
} from '../measurement/feat-estimate-measurements-v2/domain/entities/estimate-measurement.model';
import { EstimateMeasurementsService } from '../measurement/feat-estimate-measurements-v2/estimate-measurements.service';
import { EstimatePlanMeasurementV2, EstimatePlansV2StoresService } from '../plans-widget/feat-estimate-plans-v2/domain';
import { EstimatePlansV2Service } from '../plans-widget/feat-estimate-plans-v2/estimate-plans-v2.service';
import { AddPlanMeasurementResult } from './api/add-plan-measurement.generated';
import { AutoMeasureResult } from './api/auto-measure.generated';
import { BulkUpsertEstimateMeasurementsResult } from './api/bulk-upsert-estimate-measurement.generated';
import { AutoMeasureApiService } from './auto-measure-api.service';
import { AutoMeasureLoadingComponent } from './auto-measure-loading/auto-measure-loading.component';
import { AUTO_MEASURE_TYPES, AutoMeasureType } from './entities/auto-measure-types';

@Injectable({
    providedIn: 'root',
})
export class AutoMeasureService {
    readonly #apiService = inject(AutoMeasureApiService);
    readonly #takeOffV2StoresService = inject(TakeOffV2StoresService);
    readonly #takeoffService = inject(TakeOffService);
    readonly #estimateStore = inject(EstimateViewStoresService);
    readonly #estimatePlansV2StoresService = inject(EstimatePlansV2StoresService);
    readonly #estimateMeasurementStoresService = inject(EstimateMeasurementStoresService);
    readonly #estimateMeasurementService = inject(EstimateMeasurementsService);
    readonly #bxLogger = inject(BxLogger);
    readonly #helperService = inject(HelperService);
    readonly #takeOffV2DrawingService = inject(TakeOffV2DrawingService);
    readonly #estimatePlansV2Service = inject(EstimatePlansV2Service);
    readonly #quickFeedbackService = inject(QuickFeedbackService);
    #destroy = new Subject<void>();

    planId?: Uuid;
    selectedPlan = toSignal(this.#takeOffV2StoresService.selectedPlan$);
    autoMeasureInProgress = signal<boolean>(false);
    autoMeasureType = signal<AutoMeasureType | null>(null);

    autoMeasure(type: AutoMeasureType, planId: Uuid) {
        this.autoMeasureType.set(type);
        this.planId = planId;
        const input = {
            request: {
                planId: planId,
            },
        };
        this.autoMeasureInProgress.set(true);
        this.#helperService.showModal(AutoMeasureLoadingComponent, {}, AsideModalSize.narrow);
        return this.#apiService.autoMeasurePlan(input).pipe(
            takeUntil(this.#destroy),
            switchMap((res) => {
                if (res.errors || res.data?.autoMeasurement.errors || !res.data?.autoMeasurement?.planMeasureResponse?.measurements) {
                    this.#handleError();
                    return EMPTY;
                }

                if (res.data?.autoMeasurement?.planMeasureResponse?.measurements?.length === 0) {
                    this.#bxLogger.info("We couldn't identify any rooms");
                    this.stopGeneration();
                    return EMPTY;
                }

                return this.processAutoMeasureResponse(res.data?.autoMeasurement?.planMeasureResponse);
            }),
            catchError((error) => {
                this.#handleError();
                return EMPTY;
            })
        );
    }

    processAutoMeasureResponse(planMeasurements: AutoMeasureResult['autoMeasurement']['planMeasureResponse']) {
        if (!planMeasurements?.measurements?.length) return EMPTY;

        const { drawings, measurements } = this.#createDrawingsAndMeasurements(planMeasurements.measurements);

        return this.#bulkUpdateMeasurements(measurements).pipe(
            takeUntil(this.#destroy),
            switchMap((bulkResponse) => {
                return this.addPlanMeasurement(drawings, bulkResponse);
            }),
            catchError((error) => {
                this.#handleError();
                return EMPTY;
            })
        );
    }

    #createDrawingsAndMeasurements(planMeasurements: Measurements): { drawings: Drawing[]; measurements: EstimateMeasurement[] } {
        const scaleUnit = this.selectedPlan()?.ScaleUnit as string;
        const unit = ScaleUnit.getUnit(scaleUnit);
        const hScale = new Scale(unit, this.selectedPlan()?.Scale);
        const vScale = new Scale(unit, this.selectedPlan()?.VScale);

        const currentCategoriesLength = this.#estimateMeasurementStoresService.snapshotMeasurementCategories?.length ?? 0;
        return planMeasurements.reduce(
            (acc, planMeasurement, index) => {
                const roomName = this.#getRoomName(index, planMeasurement.roomName ?? null);
                const drawing = this.getDrawing(planMeasurement.polygon, roomName, scaleUnit);
                drawing.horizontalScale = hScale;
                drawing.verticalScale = vScale;
                drawing.calculateMeasurement();

                acc.drawings.push(drawing);
                acc.measurements.push(this.generateMeasurement(roomName, currentCategoriesLength + index + 1));
                return acc;
            },
            { drawings: [] as Drawing[], measurements: [] as EstimateMeasurement[] }
        );
    }

    #getRoomName(index: number, roomName: string | null): string {
        const roomNameSuffix = this.autoMeasureType() === AUTO_MEASURE_TYPES.PERIMETER ? 'Perimeter' : 'Area';
        const baseRoomName = roomName ?? `Untitled measurement-${index + 1}`;
        return `${baseRoomName} ${roomNameSuffix}`;
    }

    getDrawing(polyon: Polygon, roomName: string, scaleUnit: string): Drawing {
        const color = this.autoMeasureType() === AUTO_MEASURE_TYPES.PERIMETER ? '#00FFFF' : '#FF0000';
        const unit =
            this.autoMeasureType() === AUTO_MEASURE_TYPES.PERIMETER
                ? this.#takeoffService.getDefaultLinealUnit()
                : this.#takeoffService.getDefaultSquareUnit();
        return new Drawing({
            shape: DrawingShape.Polygon,
            depth: 0,
            depthUnit: 'mm',
            position: new Position(polyon.positionX, polyon.positionY),
            points: this.#parsePoints(polyon.points),
            color,
            unit,
            isNegative: false,
            label: roomName,
            isHidden: false,
            planScaleUnit: scaleUnit,
        });
    }

    #parsePoints(pointString: string): Array<{ x: number; y: number }> {
        return pointString.split(';').map((pair) => {
            const parts = pair.split(',');
            if (parts.length !== 2) {
                throw new Error(`Invalid point format: ${pair}`);
            }

            const x = Number(parts[0]?.trim());
            const y = Number(parts[1]?.trim());

            if (isNaN(x) || isNaN(y)) {
                throw new Error(`Invalid number in point: ${pair}`);
            }

            return { x, y };
        });
    }

    generateMeasurement(roomName: string, order: number): EstimateMeasurement {
        const unit =
            this.autoMeasureType() === AUTO_MEASURE_TYPES.PERIMETER
                ? this.#takeoffService.getDefaultLinealUnit()
                : this.#takeoffService.getDefaultSquareUnit();
        return {
            iD_EstimateMeasurement: null,
            description: roomName,
            key: order,
            measurement: 0,
            expression: null,
            iD_PlanMeasurement: null,
            uOM: unit,
            isDeleted: false,
            isParent: false,
            parentId: null,
            measurements: null,
        };
    }

    addPlanMeasurement(selectedDrawings: Drawing[], estimateMeasurements: EstimateMeasurement[]) {
        const planMeasurements = this.#getAddPlanInput(selectedDrawings, estimateMeasurements);
        if (!planMeasurements.length) return EMPTY;

        return this.#apiService.addMeasurementforPlan(planMeasurements).pipe(
            takeUntil(this.#destroy),
            switchMap((addRes) => {
                const res = addRes.data?.addPlanMeasurements;
                if (!res?.length) return EMPTY;
                return this.mapPlanToEstimateMeasurement(res, estimateMeasurements);
            }),
            catchError((error) => {
                this.#handleError();
                return EMPTY;
            })
        );
    }

    #getAddPlanInput(drawings: Drawing[], estMeasurement: EstimateMeasurement[]): AddPlanMeasurementModelInput[] {
        const planId = this.planId;
        if (!planId) return [];

        let planMeasurements: AddPlanMeasurementModelInput[] = [];

        const idLookup = estMeasurement.reduce(
            (acc, m) => {
                (acc[m.description] ??= []).push(m.iD_EstimateMeasurement ?? '');
                return acc;
            },
            {} as Record<string, string[]>
        );

        for (const drawing of drawings) {
            const description = drawing.label;
            const availableIds = idLookup[description];

            if (!availableIds?.length) continue;
            // remove matched measurement from the available measurements as measurements can have the same description
            const measurementId = availableIds.shift();
            if (!measurementId) continue;

            planMeasurements.push({
                color: drawing.color,
                depth: drawing.depth,
                depthUnit: drawing.depthUnit,
                description,
                iD_Measurement: measurementId as Uuid,
                iD_Plan: planId,
                isNegative: false,
                measurement: drawing.measurement,
                measurementUnit: drawing.unit,
                pitch: drawing.pitch,
                points: drawing.pointsToString(),
                position: this.#getStringFromPosition(drawing.position),
                shapeType: drawing.shape,
            });
        }

        return planMeasurements;
    }

    #getStringFromPosition(val: { x: number; y: number }) {
        if (val?.x && val?.y) return val.x.toString() + ',' + val.y.toString();
        else return '';
    }

    mapPlanToEstimateMeasurement(addResponse: AddPlanMeasurementResult['addPlanMeasurements'], estimateMeasurement: EstimateMeasurement[]) {
        const addedPlanMeasurements = addResponse?.filter((pm) => !!pm);
        if (!addedPlanMeasurements?.length) return EMPTY;

        this.#updateSelectedPlan(addedPlanMeasurements);

        let updatedEstimateMeasurement: EstimateMeasurement[] = [];
        estimateMeasurement.forEach((m) => {
            const planMes = addedPlanMeasurements.find((pm) => pm?.iD_Measurement === m.iD_EstimateMeasurement);
            if (!planMes?.iD_Measurement) return;
            updatedEstimateMeasurement.push({
                ...m,
                iD_PlanMeasurement: planMes.iD_Measurement,
                measurement: planMes.measurement,
                expression: planMes.measurement.toString(),
            });
        });
        if (!updatedEstimateMeasurement?.length) return EMPTY;
        return this.#bulkUpdateMeasurements(updatedEstimateMeasurement).pipe(
            switchMap((updatedMeasurements) => {
                const addedMeasurements = this.#transformToViewModel(updatedMeasurements);
                const existingCategories = this.#estimateMeasurementStoresService.snapshotMeasurementCategories;
                this.#estimateMeasurementStoresService.measurementCategories = [...existingCategories, ...addedMeasurements];
                this.#estimateMeasurementStoresService.totalMeasurements = addedMeasurements.length + existingCategories.length;

                return this.#estimatePlansV2Service.getEstimatePlansByBreeze().pipe(
                    tap(() => {
                        this.#takeOffV2StoresService.selectedPlanDrawings = this.#takeOffV2DrawingService.getDrawingsForPlan();
                        this.#quickFeedbackService.showQuickFeedback.set(true);
                        if (this.planId)
                            this.#quickFeedbackService.quickFeedbackContext.set({
                                type: 'plan',
                                id: this.planId,
                                metadata: {
                                    estimateId: this.#estimateStore.snapshotEstimate.ID_Estimate,
                                },
                            });
                        this.stopGeneration();
                    })
                );
            })
        );
    }

    #updateSelectedPlan(addedPlanMeasurements: AddPlanMeasurementResult['addPlanMeasurements']) {
        const updatedPlanMeasurements: EstimatePlanMeasurementV2[] = [];
        addedPlanMeasurements?.forEach((planMeasurement) => {
            updatedPlanMeasurements.push({
                measurementId: planMeasurement?.iD_Measurement ?? '',
                planMeasurementId: planMeasurement?.iD_PlanMeasurement ?? '',
                isDeleted: false,
            });
        });

        const plan = this.#estimatePlansV2StoresService.snapshotSelectedEstimatePlan;
        const updatedPlan = { ...plan, isSelected: true, measurements: [...plan.measurements, ...updatedPlanMeasurements] };
        this.#estimatePlansV2StoresService.updateEstimatePlan(updatedPlan);
    }

    #bulkUpdateMeasurements(estimateMeasurement: EstimateMeasurement[]): Observable<EstimateMeasurement[]> {
        const estimateId = this.#estimateStore.snapshotEstimate.ID_Estimate;
        return this.#apiService.bulkUpsertEstimateMeasurements(estimateId, estimateMeasurement).pipe(
            takeUntil(this.#destroy),
            map((response) => this.#convertToEstimateMeasurement(response.data?.bulkUpsertEstimateMeasurements)),
            catchError((error) => {
                this.#handleError();
                return EMPTY;
            })
        );
    }

    #convertToEstimateMeasurement(m: BulkUpsertEstimateMeasurementsResult['bulkUpsertEstimateMeasurements']): EstimateMeasurement[] {
        return (m || [])
            .filter((m) => !!m)
            .map((m) => ({
                ...m,
                measurements: null,
                iD_PlanMeasurement: m.iD_PlanMeasurement ?? null,
                description: m.description ?? '',
                uOM: m.uOM ?? '',
                expression: m.expression ?? '',
                parentId: m.parentId ?? null,
            }));
    }

    #transformToViewModel(measurements?: EstimateMeasurement[]): EstimateMeasurementVM[] {
        return (measurements || [])
            .filter((m) => !!m)
            .map((m) =>
                this.#estimateMeasurementService.mapToEstimateMeasurement(this.#estimateStore.snapshotEstimate.ID_Estimate, m, true)
            );
    }

    #handleError() {
        this.#bxLogger.error('An error occurred while auto measuring. Please try again, or contact support.');
        this.stopGeneration();
    }

    stopGeneration() {
        this.autoMeasureInProgress.set(false);
        this.#destroy.next();
        this.#destroy = new Subject<void>();
    }
}

type Polygon = NonNullable<AutoMeasureResult['autoMeasurement']['planMeasureResponse']>['measurements'][number]['polygon'];
type Measurements = NonNullable<AutoMeasureResult['autoMeasurement']['planMeasureResponse']>['measurements'];
